import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const CustomButton = ({ 
  children, 
  onClick, 
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  responsive = false,
  disabled = false,
  type = 'button'
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!responsive) return;

    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Usando ResizeObserver para detectar mudanças no container pai
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        setIsMobile(width < 768);
      }
    });

    if (buttonRef.current && buttonRef.current.parentElement) {
      resizeObserver.observe(buttonRef.current.parentElement);
    }

    // Adiciona listener para resize da janela como fallback
    window.addEventListener('resize', checkScreenSize);

    return () => {
      if (buttonRef.current && buttonRef.current.parentElement) {
        resizeObserver.unobserve(buttonRef.current.parentElement);
      }
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [responsive]);

  // Estilos base
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Montserrat", sans-serif',
    opacity: disabled ? 0.6 : 1,
    ':hover': disabled ? {} : {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)'
    }
  };

  // Variantes de cor
  const variants = {
    primary: {
      backgroundColor: '#09448C',
      color: '#fff',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      ':hover': disabled ? {} : {
        backgroundColor: '#07316e'
      }
    },
    secondary: {
      backgroundColor: 'transparent',
      color: '#09448C',
      border: '1px solid #09448C',
      ':hover': disabled ? {} : {
        backgroundColor: '#f1f8ff'
      }
    },
    danger: {
      backgroundColor: '#dc3545',
      color: '#fff',
      ':hover': disabled ? {} : {
        backgroundColor: '#bb2d3b'
      }
    }
  };

  // Tamanhos
  const sizes = {
    small: {
      padding: '8px 16px',
      fontSize: '14px'
    },
    medium: {
      padding: '12px 24px',
      fontSize: '16px'
    },
    large: {
      padding: '16px 32px',
      fontSize: '18px'
    }
  };

  // Combinando estilos
  const buttonStyle = {
    ...baseStyle,
    ...variants[variant],
    ...sizes[size],
    width: (responsive && isMobile) || fullWidth ? '100%' : 'auto',
    margin: '0 auto'
  };

  return (
    <button
      ref={buttonRef}
      style={buttonStyle}
      onClick={onClick}
      disabled={disabled}
      type={type}
    >
      {children}
    </button>
  );
};

CustomButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  responsive: PropTypes.bool, // Nova prop para ativar responsividade automática
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset'])
};

CustomButton.defaultProps = {
  responsive: true // Por padrão, a responsividade está ativada
};

export default CustomButton;